/**
 * Campaign-specific type mappings for audit logs.
 *
 * These mappings translate raw backend campaign values to human-readable display labels
 * that match the Customer.io UI terminology.
 */

/**
 * Campaign trigger type labels
 * Maps backend campaign type values to user-friendly display names
 * Labels match VWB trigger-selection component from ~/code/ui
 * Note: Values can be lowercase (seg_attr) or PascalCase (SegAttr) depending on source
 */
export const CampaignTypeLabels: Record<string | number, string> = {
  // Lowercase versions (common in serialized data)
  seg_attr: 'Attribute or Segment',
  transactional: 'Event',
  behavioral: 'Segment change',
  triggered_broadcast: 'API triggered broadcast',
  date: 'Important date',
  form: 'Form submission',
  webhook: 'Webhook',
  object: 'Object updated',
  relationship: 'Relationship changed',
  none: 'No trigger',
  // PascalCase versions (from backend Go structs)
  SegAttr: 'Attribute or Segment',
  Transactional: 'Event',
  Behavioral: 'Segment change',
  TriggeredBroadcast: 'API triggered broadcast',
  Date: 'Important date',
  Form: 'Form submission',
  Webhook: 'Webhook',
  Object: 'Object updated',
  Relationship: 'Relationship changed',
  None: 'No trigger',

  // Numeric CampaignType values from services/campaigns/configuration.proto
  0: 'Attribute or Segment', // Customer
  1: 'Event',
  2: 'API triggered broadcast', // TriggeredBroadcast
  3: 'Newsletter', // None
  4: 'Important date', // Date
  5: 'Transactional Message',
  6: 'Form submission', // FormEvent
  7: 'Webhook', // WebhookTriggered
  8: 'Relationship changed', // Relationship
  9: 'Object updated', // Object
  10: 'V3 Campaign',
  11: 'Attribute or Segment', // SegAttr
};

/**
 * Campaign state labels
 * Maps backend campaign state values to user-friendly display names
 */
export const CampaignStateLabels: Record<string, string> = {
  running: 'Running',
  draft: 'Draft',
  stopped: 'Stopped',
  archived: 'Archived',
  sunsetting: 'Stopping',
  stopping: 'Stopping',
  archiving: 'Archiving',
  unarchiving: 'Unarchiving',
};

/**
 * Campaign restart mode labels (UI uses "Frequency" section)
 * Maps backend restart_mode values to user-friendly display names
 * Labels match VWB frequency-controls component options
 * Supports both string and numeric values
 */
export const RestartModeLabels: Record<string | number, string> = {
  none: 'One time',
  rematch: 'Every re-match',
  interval: 'At fixed intervals',
  // Numeric equivalents
  0: 'One time',
  1: 'Every re-match',
  2: 'At fixed intervals',
};

/**
 * Conversion type labels (UI uses "conversion criteria")
 * Maps backend conversion_type values to user-friendly display names
 */
export const ConversionTypeLabels: Record<string, string> = {
  enter: 'Person enters segment',
  exit: 'Person exits segment',
  perform_event: 'Person performs event',
};

/**
 * Conversion action labels
 * Maps backend conversion_action values to user-friendly display names
 * These describe what message interaction triggers a conversion
 */
export const ConversionActionLabels: Record<string, string> = {
  // Lowercase versions
  receiving: 'being sent a message',
  opening: 'opening a message',
  clicking: 'clicking a tracked link in a message',
  // PascalCase/Title case versions (from backend)
  Receiving: 'being sent a message',
  Opened: 'opening a message',
  Opening: 'opening a message',
  Clicked: 'clicking a tracked link in a message',
  Clicking: 'clicking a tracked link in a message',
};

/**
 * Conversion timeframe labels
 * Maps backend conversion timeframe type values to user-friendly display names
 */
export const ConversionTimeframeLabels: Record<string, string> = {
  within: 'Within timeframe',
  any_time: 'Any time (no limit)',
};

/**
 * Date triggered frequency labels
 */
export const DateTriggeredFrequencyLabels: Record<string, string> = {
  once: 'Only once, on this date',
  monthly: 'Every month',
  yearly: 'Every year',
};

/**
 * Campaign update type labels
 * Describes what kind of update was made to a campaign
 */
export const CampaignUpdateTypeLabels: Record<string, string> = {
  main: 'Main settings',
  state: 'State change',
  exit_and_conversion: 'Exit and conversion settings',
  conversion: 'Conversion settings',
  exit_conditions: 'Exit conditions',
  recipients: 'Recipients',
  edges: 'Workflow connections',
  add_actions: 'Actions added',
  delete_actions: 'Actions deleted',
  save_action: 'Action saved',
  send_to_unsubs: 'Subscription settings',
  global_settings: 'Global settings',
};

/**
 * Comparison operator labels
 * Used when comparing attribute values in campaign action conditions
 */
export const ComparisonOperatorLabels: Record<string, string> = {
  value: 'a specific value',
  customer_attribute: "the person's attribute",
  event_attribute: 'the trigger event attribute',
  foreign_event_attribute: 'the foreign event attribute',
  trigger_object_attribute: 'the trigger object attribute',
  trigger_relationship_attribute: 'the trigger relationship attribute',
  relationship_attribute: 'the relationship attribute',
  trigger_person_attribute: "the trigger person's attribute",
};

/**
 * Object trigger type labels
 */
export const ObjectTriggerTypeLabels: Record<number | string, string> = {
  0: 'Created',
  1: 'Deleted',
  2: 'Updated',
};

/**
 * Object relationship trigger type labels
 */
export const ObjectRelationshipTriggerTypeLabels: Record<number | string, string> = {
  0: 'Relationship added',
  1: 'Relationship deleted',
  2: 'Relationship changed',
};

/**
 * Audience settings type labels
 */
export const AudienceSettingsTypeLabels: Record<number | string, string> = {
  0: 'The triggering person',
  1: 'Every person related to the object',
  2: 'Certain people in the account',
};

/**
 * Data action condition type labels
 */
export const DataActionConditionTypeLabels: Record<string, string> = {
  trigger_attribute: 'Trigger attribute',
  profile_attribute: 'Profile attribute',
  event_attribute: 'Event attribute',
  liquid: 'Liquid',
  static: 'Static value',
  remove_attribute: 'Remove attribute',
  javascript: 'JavaScript',
  trigger_relationship_attribute: 'Trigger relationship attribute',
  trigger_object_attribute: 'Trigger object attribute',
};

/**
 * Check if a campaign is an API-Triggered Broadcast based on its type value.
 * Handles various formats: numeric (2), string ('2'), lowercase ('triggered_broadcast'),
 * PascalCase ('TriggeredBroadcast'), and mixed case variations.
 */
export function isAPITriggeredBroadcast(typeValue: string | number | null | undefined): boolean {
  if (typeValue == null) {
    return false;
  }
  // Check for numeric type 2 (API-Triggered Broadcast)
  if (typeValue === 2 || typeValue === '2') {
    return true;
  }
  // Normalize string by removing underscores and converting to lowercase for comparison
  // This handles: "triggered_broadcast", "TriggeredBroadcast", "triggeredbroadcast", etc.
  const typeString = String(typeValue).toLowerCase().replace(/_/g, '');
  return typeString === 'triggeredbroadcast';
}

/**
 * Get the display label for a campaign type.
 * Returns null if no label is found (instead of raw value which might be meaningless).
 */
export function getCampaignTypeLabel(typeValue: string | number | null | undefined): string | null {
  if (typeValue == null) {
    return null;
  }

  // Direct lookup
  const label = CampaignTypeLabels[typeValue];
  if (label != null && label !== '') {
    return label;
  }

  // If numeric, try string conversion for lookup
  if (typeof typeValue === 'number') {
    const stringLabel = CampaignTypeLabels[String(typeValue)];
    if (stringLabel != null && stringLabel !== '') {
      return stringLabel;
    }
  }

  return null;
}
